// pages/BlogList.jsx
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Container, Row, Col, Card, CardBody, Spinner, Alert } from 'reactstrap'
import { Zap } from 'lucide-react'
import { fetchBlogPosts } from '../../helpers/blogApi'
import SEO from '../../components/Common/SEO'
import { PAGE_SEO } from '../../config/seoConfig'
import { generateCanonicalUrl } from '../../utils/seoUtils'
import { useAnalytics, ANALYTICS_EVENTS } from '../../analytics'


const BlogList = () => {
    const navigate = useNavigate()
    const { trackEvent } = useAnalytics()
    const [posts, setPosts] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [categories, setCategories] = useState([])
    const [categoriesLoading, setCategoriesLoading] = useState(true)
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 9,
        total: 0
    })
    const [activeCategory, setActiveCategory] = useState('all')

    useEffect(() => {
        loadPosts()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page])

    useEffect(() => {
        // Reset to page 1 when category changes
        setPagination(prev => ({ ...prev, page: 1 }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCategory])

    useEffect(() => {
        loadPosts()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCategory])

    useEffect(() => {
        loadAllCategories()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadAllCategories = async () => {
        try {
            setCategoriesLoading(true)
            // Load all posts first to get all categories
            const allPostsResponse = await fetchBlogPosts({ page: 1, pageSize: 100 })
            const allPosts = allPostsResponse.data
            
            // Extract categories from all posts
            const extractedCategories = allPosts.reduce((acc, post) => {
                const category = post.category || {}
                // Handle both string category names and object categories
                if (typeof category === 'string' && category && !acc.find(cat => cat.slug === category.toLowerCase().replace(/\s+/g, '-'))) {
                    const slug = category.toLowerCase().replace(/\s+/g, '-')
                    acc.push({ 
                        slug: slug, 
                        name: category 
                    })
                } else if (category?.slug && category?.name && !acc.find(cat => cat.slug === category.slug)) {
                    acc.push({ slug: category.slug, name: category.name })
                }
                return acc
            }, [])
            setCategories(extractedCategories)
        } catch (err) {
            setCategories([])
        } finally {
            setCategoriesLoading(false)
        }
    }

    const loadPosts = async () => {
        try {
            setLoading(true)
            console.log('loadPosts called with activeCategory:', activeCategory)
            const params = {
                page: pagination.page,
                pageSize: pagination.pageSize
            }
            
            // Add category parameter if not 'all'
            if (activeCategory !== 'all') {
                params.category = activeCategory
                console.log('Adding category to params:', params)
            }
            
            console.log('Final API params:', params)
            const response = await fetchBlogPosts(params)
            console.log('API response for category', activeCategory, ':', response.data.length, 'posts')
            setPosts(response.data)
            setPagination((prev) => ({
                ...prev,
                total: response.meta?.pagination?.total || 0
            }))
            setError(null)
        } catch (err) {
            console.error('Error loading blog posts:', err)
            setError('Failed to load blog posts. Please try again later.')
        } finally {
            setLoading(false)
        }
    }

    
    const getImageUrl = (post) => {
        // Handle common custom API image fields
        return post.cover || post.cover_image || post.image || post.thumbnail || post.cover?.url || null
    }

    const formatDate = (dateString) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    const getExcerpt = (post) => {
        return post.description || post.excerpt || post.summary || ''
    }

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^\w\s-]/g, '') // Remove special characters
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Replace multiple hyphens with single
            .trim()
    }

    const getPostUrl = (post) => `/blog/${post.slug || generateSlug(post.title)}`

    const trackPostSelect = (post) => {
        const category = post.category || {}
        trackEvent(ANALYTICS_EVENTS.CONTENT.BLOG_POST_SELECTED, {
            post_title: post.title,
            post_slug: post.slug || generateSlug(post.title),
            category: category?.name || category,
            source_surface: "blog_list",
        })
    }

    const totalPages = Math.max(
        1,
        Math.ceil(pagination.total / pagination.pageSize)
    )


    return (
        <React.Fragment>
            <SEO
                title={PAGE_SEO.blog.title}
                description={PAGE_SEO.blog.description}
                keywords={PAGE_SEO.blog.keywords}
                canonical={generateCanonicalUrl('/blog')}
                robots={PAGE_SEO.blog.robots}
                pageType="blog"
            />

            <div className="page-content blog-list-container">
                <Container fluid>
                    <div style={{ marginBottom: '24px' }}>
                        <h1 style={{ fontFamily: 'Rubik', fontWeight: 700, fontSize: '32px', color: '#fff', margin: 0 }}>
                            KeyQuest Blog
                        </h1>
                        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', marginTop: '8px', marginBottom: 0 }}>
                            VR industry insights, Meta Quest trends, and analytics deep-dives from the KeyQuest team.
                        </p>
                    </div>
                    {loading ? (
                        <div className="text-center py-5 blog-loading">
                            <Spinner color="primary" />
                            <p className="mt-3">Loading blog posts...</p>
                        </div>
                    ) : error ? (
                        <Alert color="danger">{error}</Alert>
                    ) : posts.length === 0 ? (
                        <Alert color="info">No blog posts found.</Alert>
                    ) : (
                        <>
                            {/* Category filter bar */}
                            {categories.length > 0 && (
                                <Row className="mb-4">
                                    <Col>
                                        <div className="blog-category-filter d-flex flex-wrap align-items-center justify-content-between gap-2">
                                            <div className="d-flex flex-wrap align-items-center gap-2">
                                            {categoriesLoading ? (
                                                <Spinner size="sm" color="primary" />
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        className={`btn btn-sm blog-category-pill ${activeCategory === 'all'
                                                                ? 'blog-category-pill-active'
                                                                : ''
                                                            }`}
                                                        onClick={() => {
                                                            trackEvent(ANALYTICS_EVENTS.CONTENT.BLOG_CATEGORY_FILTERED, { category: 'all' })
                                                            setActiveCategory('all')
                                                        }}
                                                    >
                                                        All
                                                    </button>
                                                    {categories.map((cat) => (
                                                        <button
                                                            key={cat.slug}
                                                            type="button"
                                                            className={`btn btn-sm blog-category-pill ${activeCategory === cat.slug
                                                                    ? 'blog-category-pill-active'
                                                                    : ''
                                                                }`}
                                                            onClick={() => {
                                                                trackEvent(ANALYTICS_EVENTS.CONTENT.BLOG_CATEGORY_FILTERED, { category: cat.slug })
                                                                setActiveCategory(cat.slug)
                                                            }}
                                                        >
                                                            {cat.name}
                                                        </button>
                                                    ))}
                                                </>
                                            )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            
                            <Row>
                                {posts.map((post) => {
                                    const author = post.author || {}
                                    const category = post.category || {}

                                    return (
                                        <Col lg={4} md={6} key={post.id} className="mb-4">
                                            <Card
                                                className="blog-card h-100"
                                                role="link"
                                                tabIndex={0}
                                                onClick={() => {
                                                    trackPostSelect(post)
                                                    navigate(getPostUrl(post))
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault()
                                                        trackPostSelect(post)
                                                        navigate(getPostUrl(post))
                                                    }
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {getImageUrl(post) && (
                                                    <div className="blog-image-wrapper">
                                                        <img
                                                            src={getImageUrl(post)}
                                                            alt={post.title || 'Blog post'}
                                                            className="card-img-top"
                                                            style={{
                                                                width: '100%',
                                                                height: 'auto',
                                                                display: 'block',
                                                                borderRadius: '8px 8px 0 0'
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                <CardBody className="d-flex flex-column">
                                                    <div className="mb-2 d-flex flex-wrap align-items-center">
                                                        <small className="text-muted">
                                                            {formatDate(post.published_at || post.publishedAt || post.created_at)}
                                                        </small>
                                                        {author?.name && (
                                                            <small className="text-muted ms-3">
                                                                By {author.name}
                                                            </small>
                                                        )}
                                                        {category?.name && (
                                                            <span
                                                                className="badge category-tag ms-3"
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    trackEvent(ANALYTICS_EVENTS.CONTENT.BLOG_CATEGORY_FILTERED, { category: category.slug })
                                                                    setActiveCategory(category.slug)
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.stopPropagation()
                                                                        e.preventDefault()
                                                                        trackEvent(ANALYTICS_EVENTS.CONTENT.BLOG_CATEGORY_FILTERED, { category: category.slug })
                                                                        setActiveCategory(category.slug)
                                                                    }
                                                                }}
                                                            >
                                                                {category.name}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <h2 className="card-title">
                                                        <Link
                                                            to={getPostUrl(post)}
                                                            className="text-dark"
                                                            onClick={(e) => { e.stopPropagation(); trackPostSelect(post) }}
                                                        >
                                                            {post.title}
                                                        </Link>
                                                    </h2>

                                                    <p className="card-text text-muted flex-grow-1">
                                                        {getExcerpt(post)}
                                                    </p>

                                                    <Link
                                                        to={getPostUrl(post)}
                                                        className="btn btn-primary btn-sm mt-auto"
                                                        onClick={(e) => { e.stopPropagation(); trackPostSelect(post) }}
                                                    >
                                                        Read More →
                                                    </Link>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    )
                                })}
                            </Row>

                            {pagination.total > pagination.pageSize && (
                                <Row className="mt-4 blog-pagination">
                                    <Col>
                                        <div className="d-flex justify-content-center">
                                            <button
                                                className="btn btn-outline-primary me-2"
                                                disabled={pagination.page === 1}
                                                onClick={() =>
                                                    setPagination((prev) => ({
                                                        ...prev,
                                                        page: prev.page - 1
                                                    }))
                                                }
                                            >
                                                Previous
                                            </button>

                                            <span className="align-self-center mx-3">
                                                Page {pagination.page} of {totalPages}
                                            </span>

                                            <button
                                                className="btn btn-outline-primary ms-2"
                                                disabled={pagination.page >= totalPages}
                                                onClick={() =>
                                                    setPagination((prev) => ({
                                                        ...prev,
                                                        page: prev.page + 1
                                                    }))
                                                }
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </>
                    )}
                </Container>
            </div>
        </React.Fragment>
    )
}

export default BlogList
